// ============================================================================
//  GarmentStandards.js
//  Industry-standard pattern data for four product categories.
//
//  Based on:
//   • ISO 4416 (men's grading), ISO 3636 (men's size designation)
//   • ASTM D5585 (sizing)
//   • Standard trade practice for Indian/export garment manufacturing
//
//  Structure per product:
//    sizes          – size run with chest/waist as key dimension
//    baseSize       – the pattern master size
//    baseMeasurements – full spec for the base size (finished garment)
//    gradeDeltas    – increment PER SIZE STEP (apply as +delta for each step above base,
//                     -delta for each step below base)
//    pieces         – pattern piece list with:
//                       measurements  (base size, used by buildDisplayPoly)
//                       seamAllowances
//                       notchFractions (nx 0-1 along each edge, as {edge,t})
//                       description
// ============================================================================

// ── Size step table (key dimension = half chest for upper body, half waist for trousers)
const SIZE_STEPS = {
  SHIRT:   { XS: -2, S: -1, M: 0, L: 1, XL: 2, XXL: 3 },   // step ref = M (38)
  TROUSER: { '28': -2, '30': -1, '32': 0, '34': 1, '36': 2, '38': 3 }, // step ref = 32
  TSHIRT:  { XS: -2, S: -1, M: 0, L: 1, XL: 2, XXL: 3 },
  POLO:    { XS: -2, S: -1, M: 0, L: 1, XL: 2, XXL: 3 },
};

// ============================================================================
//  1. FORMAL / DRESS SHIRT
// ============================================================================
const SHIRT = {
  label: 'Formal / Dress Shirt',
  type:  'SHIRT',
  baseSize: 'M',
  sizes: ['XS', 'S', 'M', 'L', 'XL', 'XXL'],
  sizeSteps: SIZE_STEPS.SHIRT,

  // ── Finished garment base measurements (size M / chest 104 cm) ────────────
  baseMeasurements: {
    halfChest:       52,   // cm (half measurement)
    halfBackWidth:   22,   // cm
    shoulderWidth:   45,   // cm (full)
    bodyLength:      74,   // cm (CB neck to hem)
    sleeveLength:    61,   // cm (shoulder point to cuff edge)
    cuffLength:      24,   // cm (finished, folded)
    cuffHeight:       7,   // cm
    collarSize:      39,   // cm (neck circumference)
    collarHeight:     3.5, // cm
    collarStandHeight:3.0, // cm
    armholeDepth:    24,   // cm
    yokeWidth:       45,   // cm (full)
    yokeHeight:       7,   // cm
    frontBandWidth:   2.5, // cm
    chestToWaist:    21,   // cm (armhole to waist)
    halfWaist:       47,   // cm
  },

  // ── Grade increments per size step ────────────────────────────────────────
  // A size step = one size up (e.g. M→L). Apply as +N per step above base,
  // -N per step below base.
  gradeDeltas: {
    halfChest:        2.0,  // +2 each side = +4 chest total per step
    halfBackWidth:    1.0,
    shoulderWidth:    0.75,
    bodyLength:       0.75,
    sleeveLength:     0.75,
    cuffLength:       0.75,
    cuffHeight:       0,
    collarSize:       1.0,
    collarHeight:     0,
    collarStandHeight:0,
    armholeDepth:     0.5,
    yokeWidth:        0.75,
    yokeHeight:       0,
    frontBandWidth:   0,
    chestToWaist:     0.25,
    halfWaist:        2.0,
  },

  // ── Pattern pieces ─────────────────────────────────────────────────────────
  pieces: [
    {
      name: 'Front Body',
      description: 'Front panel with button/buttonhole placket. Cut x2 (or x1 on fold for pullover).',
      measurements: {
        halfChest:   26,   // half chest = 52/2 — ALWAYS use halfChest, never 'width' for body pieces
        bodyLength:  74,
        shoulder:    22.5,
        armholeDepth:24,
      },
      seamAllowances: { top: 1.5, right: 1.5, bottom: 2.5, left: 1.5 },
      notchFractions: [
        { edgeIdx: 2, t: 0.42, label: 'Chest level'  },
        { edgeIdx: 2, t: 0.75, label: 'Waist level'  },
      ],
    },
    {
      name: 'Back Body',
      description: 'Back panel. Cut x1 on fold or x2 with CB seam.',
      measurements: {
        width:       26,
        bodyLength:  74,
        shoulder:    22.5,
        armholeDepth:24,
      },
      seamAllowances: { top: 1.5, right: 1.5, bottom: 2.5, left: 1.5 },
      notchFractions: [
        { edgeIdx: 2, t: 0.42, label: 'Chest level' },
      ],
    },
    {
      name: 'Yoke',
      description: 'Back yoke. Cut x2 (outer + inner facing). Usually cut on bias.',
      measurements: {
        width:       45,
        height:       7,
      },
      seamAllowances: { top: 1.5, right: 1.5, bottom: 1.5, left: 1.5 },
      notchFractions: [
        { edgeIdx: 1, t: 0.5, label: 'CB match' },
      ],
    },
    {
      name: 'Sleeve',
      description: 'One-piece sleeve. Cut x2. Pleat or ease into armhole.',
      measurements: {
        width:       21,   // half sleeve width at cap
        sleeveLength:61,
        cuffWidth:   12.5, // half cuff opening
      },
      seamAllowances: { top: 1.5, right: 1.5, bottom: 1.5, left: 1.5 },
      notchFractions: [
        { edgeIdx: 0, t: 0.5,  label: 'Crown / cap top'     },
        { edgeIdx: 1, t: 0.35, label: 'Front armhole match' },
        { edgeIdx: 3, t: 0.35, label: 'Back armhole match'  },
      ],
    },
    {
      name: 'Cuff',
      description: 'Folded cuff. Cut x4 (2 outer + 2 inner/facing). French cuff = double height.',
      measurements: {
        width:       24,
        height:       7,
      },
      seamAllowances: { top: 1, right: 1, bottom: 1, left: 1 },
      notchFractions: [
        { edgeIdx: 0, t: 0.5, label: 'CB cuff fold' },
      ],
    },
    {
      name: 'Collar',
      description: 'Top collar. Cut x2 (outer + under collar, usually on cross grain).',
      measurements: {
        width:       20.5, // half collar length
        height:       6.5,
      },
      seamAllowances: { top: 0.7, right: 0.7, bottom: 0.7, left: 0.7 },
      notchFractions: [
        { edgeIdx: 0, t: 0.5, label: 'CB collar' },
      ],
    },
    {
      name: 'Collar Stand',
      description: 'Neck band / collar stand. Cut x2 (inner + outer). Attaches to neckline.',
      measurements: {
        width:       19.5,
        height:       3.5,
      },
      seamAllowances: { top: 0.7, right: 0.7, bottom: 0.7, left: 0.7 },
      notchFractions: [
        { edgeIdx: 0, t: 0.5,  label: 'CB stand'          },
        { edgeIdx: 1, t: 0.15, label: 'Button position'   },
        { edgeIdx: 3, t: 0.15, label: 'Buttonhole position'},
      ],
    },
    {
      name: 'Front Placket',
      description: 'Button/buttonhole band. Cut x2 (one each side). 2.5cm finished width.',
      measurements: {
        width:        5,
        height:       74,
      },
      seamAllowances: { top: 1, right: 1, bottom: 1, left: 1 },
      notchFractions: [],
    },
  ],
};

// ============================================================================
//  2. TROUSERS
// ============================================================================
const TROUSER = {
  label: 'Trousers (Flat Front)',
  type:  'TROUSER',
  baseSize: '32',
  sizes: ['28', '30', '32', '34', '36', '38'],
  sizeSteps: SIZE_STEPS.TROUSER,

  baseMeasurements: {
    halfWaist:      40,   // cm (32" = 81cm / 2 ≈ 40.5, rounded)
    halfHip:        50,   // cm
    frontRise:      28,   // cm
    backRise:       36,   // cm
    inseam:         81,   // cm
    halfThigh:      30,   // cm
    halfKnee:       21,   // cm
    halfHem:        21.5, // cm (finished)
    waistbandHeight: 4.0, // cm
    seatDepth:      22,   // cm
    crotchCurveDepth:3.5, // cm
  },

  gradeDeltas: {
    halfWaist:       2.0,
    halfHip:         2.0,
    frontRise:       0.5,
    backRise:        0.75,
    inseam:          0.5,
    halfThigh:       1.0,
    halfKnee:        0.75,
    halfHem:         0.5,
    waistbandHeight: 0,
    seatDepth:       0.5,
    crotchCurveDepth:0,
  },

  pieces: [
    {
      name: 'Front Panel',
      description: 'Front trouser panel. Cut x2. Includes pleat allowance if pleated style.',
      measurements: {
        width:       24,   // half thigh + ease at widest
        height:      109,  // front rise + inseam
        halfThigh:   30,
        frontRise:   28,
        inseam:      81,
        halfHem:     21.5,
        halfKnee:    21,
      },
      seamAllowances: { top: 1.5, right: 1.5, bottom: 3.0, left: 1.5 },
      notchFractions: [
        { edgeIdx: 0, t: 0.22, label: 'Knee'              },
        { edgeIdx: 0, t: 0.05, label: 'Fly top'           },
        { edgeIdx: 2, t: 0.5,  label: 'Side pocket mouth' },
      ],
    },
    {
      name: 'Back Panel',
      description: 'Back trouser panel. Cut x2. Back dart included in pattern.',
      measurements: {
        width:       26,   // half hip + ease
        height:      117,  // back rise + inseam
        halfHip:     50,
        backRise:    36,
        inseam:      81,
        halfHem:     21.5,
        halfKnee:    21,
        halfThigh:   32,
      },
      seamAllowances: { top: 1.5, right: 1.5, bottom: 3.0, left: 1.5 },
      notchFractions: [
        { edgeIdx: 0, t: 0.22, label: 'Knee'            },
        { edgeIdx: 2, t: 0.35, label: 'Back pocket top' },
        { edgeIdx: 2, t: 0.55, label: 'Back pocket btm' },
      ],
    },
    {
      name: 'Waistband',
      description: 'Waistband. Cut x2 (outer + facing) or x1 with fold. Width 4cm finished.',
      measurements: {
        width:       40,  // half waist
        height:       8,  // double finished height (fold)
      },
      seamAllowances: { top: 1, right: 1.5, bottom: 1, left: 1.5 },
      notchFractions: [
        { edgeIdx: 0, t: 0.5,  label: 'CB waistband'       },
        { edgeIdx: 0, t: 0.25, label: 'Side seam match R'  },
        { edgeIdx: 0, t: 0.75, label: 'Side seam match L'  },
      ],
    },
    {
      name: 'Front Pocket Bag',
      description: 'Side pocket bag. Cut x4 (2 bags). Depth typically 18-20cm.',
      measurements: {
        width:       16,
        height:      19,
      },
      seamAllowances: { top: 1, right: 1, bottom: 1, left: 1 },
      notchFractions: [],
    },
    {
      name: 'Back Welt Pocket',
      description: 'Welt pocket facing (jetting). Cut x2. Usually 14cm wide × 1.5cm jetting.',
      measurements: {
        width:       14,
        height:       4,
      },
      seamAllowances: { top: 1, right: 1, bottom: 1, left: 1 },
      notchFractions: [],
    },
    {
      name: 'Fly Piece',
      description: 'Fly extension on front right panel. Cut x1 (or mirrored for fly shield).',
      measurements: {
        width:        4,
        height:      19,
      },
      seamAllowances: { top: 1, right: 1, bottom: 1, left: 1 },
      notchFractions: [],
    },
  ],
};

// ============================================================================
//  3. T-SHIRT (Crew Neck, Short Sleeve)
// ============================================================================
const TSHIRT = {
  label: 'T-Shirt (Crew Neck, Short Sleeve)',
  type:  'TSHIRT',
  baseSize: 'M',
  sizes: ['XS', 'S', 'M', 'L', 'XL', 'XXL'],
  sizeSteps: SIZE_STEPS.TSHIRT,

  baseMeasurements: {
    halfChest:       50,   // cm (100cm chest)
    bodyLength:      70,   // cm (HPS to hem)
    shoulderWidth:   46,   // cm (full)
    sleeveLength:    21,   // cm (short sleeve)
    halfSleeveWidth: 19,   // cm
    halfNeckWidth:   9,    // cm (half neck opening)
    neckDepthFront:   9,   // cm
    neckDepthBack:    2.5, // cm
    armholeDepth:    22,   // cm
    halfHem:         49,   // cm (usually same as chest for straight cut)
  },

  gradeDeltas: {
    halfChest:       2.0,
    bodyLength:      1.0,
    shoulderWidth:   0.75,
    sleeveLength:    0.5,
    halfSleeveWidth: 0.75,
    halfNeckWidth:   0.25,
    neckDepthFront:  0,
    neckDepthBack:   0,
    armholeDepth:    0.5,
    halfHem:         2.0,
  },

  pieces: [
    {
      name: 'Front Body',
      description: 'T-shirt front. Crew neck with front neck drop. Cut x1 on fold or x2.',
      measurements: {
        width:       50,
        bodyLength:  70,
        armholeDepth:22,
        neckWidth:    9,
        neckDepth:    9,
        shoulder:    23,
      },
      seamAllowances: { top: 1.0, right: 1.0, bottom: 2.0, left: 1.0 },
      notchFractions: [
        { edgeIdx: 4, t: 0.5, label: 'Shoulder match'    },
        { edgeIdx: 2, t: 0.5, label: 'Side seam midpoint'},
      ],
    },
    {
      name: 'Back Body',
      description: 'T-shirt back. Shallower neck curve than front.',
      measurements: {
        width:       50,
        bodyLength:  70,
        armholeDepth:22,
        neckWidth:    9,
        neckDepth:    2.5,
        shoulder:    23,
      },
      seamAllowances: { top: 1.0, right: 1.0, bottom: 2.0, left: 1.0 },
      notchFractions: [
        { edgeIdx: 4, t: 0.5, label: 'Shoulder match' },
      ],
    },
    {
      name: 'Sleeve',
      description: 'Short sleeve. Cut x2. Set-in or overlock seam into armhole.',
      measurements: {
        width:       19,
        sleeveLength:21,
        cuffWidth:   15,
      },
      seamAllowances: { top: 1.0, right: 1.0, bottom: 2.0, left: 1.0 },
      notchFractions: [
        { edgeIdx: 0, t: 0.5,  label: 'Crown'            },
        { edgeIdx: 1, t: 0.3,  label: 'Front arm match'  },
        { edgeIdx: 3, t: 0.3,  label: 'Back arm match'   },
      ],
    },
    {
      name: 'Neck Rib',
      description: 'Crew neck ribband. Approx 90% of neck opening when cut (rib stretches to fit). Cut x1 on fold.',
      measurements: {
        width:       17,  // ~90% of half neck circumference (36cm total / 2 = 18, × 0.9 ≈ 17)
        height:       2.5,
      },
      seamAllowances: { top: 0, right: 1, bottom: 0, left: 1 },
      notchFractions: [
        { edgeIdx: 0, t: 0.5, label: 'CB neck' },
      ],
    },
  ],
};

// ============================================================================
//  4. POLO T-SHIRT (Piqué, Short Sleeve, Knit Collar)
// ============================================================================
const POLO = {
  label: 'Polo T-Shirt (Piqué)',
  type:  'POLO',
  baseSize: 'M',
  sizes: ['XS', 'S', 'M', 'L', 'XL', 'XXL'],
  sizeSteps: SIZE_STEPS.POLO,

  baseMeasurements: {
    halfChest:       51,
    bodyLength:      71,
    shoulderWidth:   46,
    sleeveLength:    23,
    halfSleeveWidth: 19.5,
    halfNeckWidth:   9.5,
    neckDepthFront:   4,   // shallower than T-shirt (polo has collar + placket)
    neckDepthBack:    2.5,
    armholeDepth:    22.5,
    halfHem:         50,
    collarLength:    44,   // finished collar (matches neck opening)
    collarHeight:     6,   // folded height (3cm fold + 3cm stand)
    placketLength:   21,
    placketWidth:     3.5,
  },

  gradeDeltas: {
    halfChest:        2.0,
    bodyLength:       1.0,
    shoulderWidth:    0.75,
    sleeveLength:     0.5,
    halfSleeveWidth:  0.75,
    halfNeckWidth:    0.25,
    neckDepthFront:   0,
    neckDepthBack:    0,
    armholeDepth:     0.5,
    halfHem:          2.0,
    collarLength:     1.0,
    collarHeight:     0,
    placketLength:    0.5,
    placketWidth:     0,
  },

  pieces: [
    {
      name: 'Front Body',
      description: 'Polo front. Placket opening at CF. Typically cut with CF on grain.',
      measurements: {
        width:       51,
        bodyLength:  71,
        armholeDepth:22.5,
        neckWidth:    9.5,
        neckDepth:    4,
        shoulder:    23,
      },
      seamAllowances: { top: 1.0, right: 1.0, bottom: 2.0, left: 1.0 },
      notchFractions: [
        { edgeIdx: 4, t: 0.5, label: 'Shoulder' },
        { edgeIdx: 2, t: 0.5, label: 'Side seam midpoint' },
      ],
    },
    {
      name: 'Back Body',
      description: 'Polo back. Same as T-shirt back with polo neck curve.',
      measurements: {
        width:       51,
        bodyLength:  71,
        armholeDepth:22.5,
        neckWidth:    9.5,
        neckDepth:    2.5,
        shoulder:    23,
      },
      seamAllowances: { top: 1.0, right: 1.0, bottom: 2.0, left: 1.0 },
      notchFractions: [
        { edgeIdx: 4, t: 0.5, label: 'Shoulder' },
      ],
    },
    {
      name: 'Sleeve',
      description: 'Short sleeve. Cut x2. Slightly wider than T-shirt sleeve for polo drape.',
      measurements: {
        width:       19.5,
        sleeveLength:23,
        cuffWidth:   16,
      },
      seamAllowances: { top: 1.0, right: 1.0, bottom: 2.0, left: 1.0 },
      notchFractions: [
        { edgeIdx: 0, t: 0.5,  label: 'Crown'           },
        { edgeIdx: 1, t: 0.3,  label: 'Front arm match' },
        { edgeIdx: 3, t: 0.3,  label: 'Back arm match'  },
      ],
    },
    {
      name: 'Polo Collar',
      description: 'Flat piqué collar. Cut x2 (outer + under). Width = neck opening at 100% (no stretch needed).',
      measurements: {
        width:       22,   // half collar length
        height:       7,   // folded height
      },
      seamAllowances: { top: 0.7, right: 0.7, bottom: 0.7, left: 0.7 },
      notchFractions: [
        { edgeIdx: 0, t: 0.5, label: 'CB collar' },
      ],
    },
    {
      name: 'Front Placket',
      description: 'Button placket strip. Cut x2 (buttons + buttonholes sides). 3.5cm finished width.',
      measurements: {
        width:        7,   // total placket including folded seams
        height:      21,
      },
      seamAllowances: { top: 1, right: 1, bottom: 1, left: 1 },
      notchFractions: [
        { edgeIdx: 0, t: 0.5, label: 'Placket fold line' },
      ],
    },
  ],
};

// ============================================================================
//  EXPORTS
// ============================================================================
export const GARMENT_STANDARDS = { SHIRT, TROUSER, TSHIRT, POLO };

// Get graded measurements for a specific size
export const getGradedMeasurements = (product, size) => {
  const std = GARMENT_STANDARDS[product];
  if (!std) return {};
  const steps = std.sizeSteps[size] ?? 0;
  const base  = std.baseMeasurements;
  const delta = std.gradeDeltas;
  const graded = {};
  Object.keys(base).forEach(k => {
    graded[k] = +((base[k] || 0) + (delta[k] || 0) * steps).toFixed(2);
  });
  return graded;
};

// Build all size measurements for a product
export const getAllSizeMeasurements = (product) => {
  const std = GARMENT_STANDARDS[product];
  if (!std) return {};
  const result = {};
  std.sizes.forEach(sz => { result[sz] = getGradedMeasurements(product, sz); });
  return result;
};

// Get grade delta table for MarkerMaker GradingView
// Returns { [measurement]: { [size]: delta_from_base } }
export const getGradingTable = (product) => {
  const std = GARMENT_STANDARDS[product];
  if (!std) return {};
  const table = {};
  Object.keys(std.gradeDeltas).forEach(mk => {
    table[mk] = {};
    std.sizes.forEach(sz => {
      const steps = std.sizeSteps[sz] ?? 0;
      table[mk][sz] = +((std.gradeDeltas[mk] || 0) * steps).toFixed(2);
    });
  });
  return table;
};

// Build NPF-ready pattern pieces for a product + size
export const buildStandardPatterns = (product, size = null, partId = '') => {
  const std = GARMENT_STANDARDS[product];
  if (!std) return [];
  const sz    = size || std.baseSize;
  const steps = std.sizeSteps[sz] ?? 0;
  const delta = std.gradeDeltas;

  return std.pieces.map((piece, idx) => {
    // Grade each measurement in the piece
    const gradedM = {};
    Object.entries(piece.measurements).forEach(([k, v]) => {
      const d = delta[k] || delta[Object.keys(delta).find(dk => dk.toLowerCase().includes(k.toLowerCase()))] || 0;
      gradedM[k] = +(v + d * steps).toFixed(2);
    });

    return {
      format:    'npf-v1',
      id:        `${product.toLowerCase()}-${idx}-${sz}-${Date.now()}`,
      partId,
      partName:  piece.name,
      template:  `${product.toLowerCase()}_${piece.name.toLowerCase().replace(/\s+/g,'_')}`,
      measurements:      gradedM,
      seamAllowances:    { ...piece.seamAllowances },
      grainLine:         { angleDeg: 90 },
      notches:           (piece.notchFractions || []).map((nf, ni) => ({
        id: `n-std-${ni}`,
        edgeIdx: nf.edgeIdx,
        t:       nf.t,
        label:   nf.label,
        type:    'slit',
        depth:   1,
      })),
      darts:             [],
      measurementPoints: [],
      gradingRules:      {},
      specMapping:       {},
      brandRules:        [],
      svgOutline:        '',
      tracedPoints:      null,
      sourceMethod:      `standard-${product}`,
      description:       piece.description,
      createdOn:  new Date().toISOString(),
      updatedOn:  new Date().toISOString(),
    };
  });
};

export default GARMENT_STANDARDS;

// ============================================================================
//  SEMANTIC ANCHOR POINTS
//  Named anatomical measurement points for each piece type.
//  Positions defined in pattern cm space (same coord system as buildDisplayPoly):
//    x = 0 at horizontal centre, y = 0 at bottom/hem, y = H at top/shoulder
//  These anchor points are auto-placed when a shape is generated from a template.
//  For traced shapes, they are placed proportionally and the user can drag to adjust.
// ============================================================================
export const TEMPLATE_ANCHORS = {

  body_front: (m, W, H, AD) => [
    { id:'HPS_R',    label:'HPS Right',          x: W*0.87,   y: H-2.5,   color:'#ef4444', desc:'Highest Point of Shoulder — right' },
    { id:'HPS_L',    label:'HPS Left',           x:-W*0.87,   y: H-2.5,   color:'#ef4444', desc:'Highest Point of Shoulder — left' },
    { id:'CF_neck',  label:'CF Neck',            x: 0,         y: H-(W*0.32), color:'#8b5cf6', desc:'Centre Front neck deepest point' },
    { id:'under_R',  label:'Underarm Right',     x: W,         y: H-AD,    color:'#2563eb', desc:'Right underarm / side seam at armhole' },
    { id:'under_L',  label:'Underarm Left',      x:-W,         y: H-AD,    color:'#2563eb', desc:'Left underarm / side seam at armhole' },
    { id:'waist_R',  label:'Waist Right',        x: W*0.96,   y: H-AD-(AD*0.7), color:'#f59e0b', desc:'Right side at waist level' },
    { id:'waist_L',  label:'Waist Left',         x:-W*0.96,   y: H-AD-(AD*0.7), color:'#f59e0b', desc:'Left side at waist level' },
    { id:'hem_R',    label:'Hem Right',          x: W,         y: 0,        color:'#64748b', desc:'Right hem corner' },
    { id:'hem_L',    label:'Hem Left',           x:-W,         y: 0,        color:'#64748b', desc:'Left hem corner' },
    { id:'hem_C',    label:'Hem Centre',         x: 0,         y: 0,        color:'#64748b', desc:'Centre hem' },
    { id:'sh_R',     label:'Shoulder Right',     x: W*0.87,   y: H-2.5,   color:'#ef4444', desc:'Shoulder seam end (right)' },
    { id:'sh_L',     label:'Shoulder Left',      x:-W*0.87,   y: H-2.5,   color:'#ef4444', desc:'Shoulder seam end (left)' },
  ],

  body_back: (m, W, H, AD) => [
    { id:'HPS_R',    label:'HPS Right',          x: W*0.87,   y: H-1.5,   color:'#ef4444', desc:'Highest Point of Shoulder — right' },
    { id:'HPS_L',    label:'HPS Left',           x:-W*0.87,   y: H-1.5,   color:'#ef4444' },
    { id:'CB_neck',  label:'CB Neck',            x: 0,         y: H-(W*0.095), color:'#8b5cf6', desc:'Centre Back neck' },
    { id:'under_R',  label:'Underarm Right',     x: W,         y: H-AD,    color:'#2563eb' },
    { id:'under_L',  label:'Underarm Left',      x:-W,         y: H-AD,    color:'#2563eb' },
    { id:'waist_R',  label:'Waist Right',        x: W*0.96,   y: H-AD-(AD*0.7), color:'#f59e0b' },
    { id:'waist_L',  label:'Waist Left',         x:-W*0.96,   y: H-AD-(AD*0.7), color:'#f59e0b' },
    { id:'hem_R',    label:'Hem Right',          x: W,         y: 0,        color:'#64748b' },
    { id:'hem_L',    label:'Hem Left',           x:-W,         y: 0,        color:'#64748b' },
    { id:'hem_C',    label:'Hem Centre',         x: 0,         y: 0,        color:'#64748b' },
  ],

  sleeve: (m, W, SL, capH) => [
    { id:'crown',    label:'Crown (Cap Peak)',   x: 0,         y: SL+capH,  color:'#ef4444', desc:'Top of sleeve cap — highest point' },
    { id:'under_F',  label:'Underarm Front',     x: W,         y: SL,       color:'#2563eb', desc:'Front armhole bottom (sleeve side)' },
    { id:'under_B',  label:'Underarm Back',      x:-W,         y: SL,       color:'#2563eb', desc:'Back armhole bottom (sleeve side)' },
    { id:'cap_F',    label:'Cap Notch Front',    x: W*0.4,    y: SL+capH*0.75, color:'#8b5cf6', desc:'Front notch on sleeve cap (1 notch)' },
    { id:'cap_B',    label:'Cap Notch Back',     x:-W*0.4,    y: SL+capH*0.75, color:'#8b5cf6', desc:'Back notch on sleeve cap (2 notches)' },
    { id:'cuff_R',   label:'Cuff Right',         x: W*0.7,    y: 0,         color:'#64748b' },
    { id:'cuff_L',   label:'Cuff Left',          x:-W*0.7,    y: 0,         color:'#64748b' },
    { id:'cuff_C',   label:'Cuff Centre',        x: 0,         y: 0,         color:'#64748b' },
  ],

  trouser_front: (m, HH, H, IS, FR) => [
    { id:'wst_side', label:'Waist Side',         x: HH*0.97,  y: H,         color:'#ef4444', desc:'Waist at side seam' },
    { id:'wst_CF',   label:'Waist CF',           x: 0.5,      y: H+1,       color:'#ef4444', desc:'Centre Front waist (fly peak)' },
    { id:'hip_side', label:'Hip Right',          x: HH,       y: IS,        color:'#dc2626', desc:'Hip at side seam' },
    { id:'crotch_tip',label:'Crotch Point',      x:-FR*0.35,  y: H-FR*0.35, color:'#8b5cf6', desc:'Deepest point of crotch curve' },
    { id:'knee_R',   label:'Knee Right',         x: HH*0.85,  y: IS*0.38,   color:'#f59e0b', desc:'Right knee at side seam' },
    { id:'knee_L',   label:'Knee Left',          x:-HH*0.65,  y: IS*0.38,   color:'#f59e0b', desc:'Left knee at inseam' },
    { id:'hem_side', label:'Hem Side',           x: HH*0.7,   y: 0,         color:'#64748b' },
    { id:'hem_in',   label:'Hem Inseam',         x:-HH*0.7,   y: 0,         color:'#64748b' },
  ],

  trouser_back: (m, HH, H, IS, BR) => [
    { id:'wst_side', label:'Waist Side',         x: HH*0.9,   y: H-0.5,    color:'#ef4444' },
    { id:'wst_CB',   label:'Waist CB',           x:-HH*0.1,   y: H,         color:'#ef4444', desc:'Centre Back waist' },
    { id:'hip_side', label:'Hip Right',          x: HH,       y: IS,        color:'#dc2626' },
    { id:'crotch_tip',label:'Crotch Point',      x:-BR*0.55,  y: H-BR*0.4,  color:'#8b5cf6', desc:'Deepest point of back crotch curve' },
    { id:'knee_R',   label:'Knee Right',         x: HH*0.88,  y: IS*0.38,   color:'#f59e0b' },
    { id:'knee_L',   label:'Knee Left',          x:-HH*0.72,  y: IS*0.38,   color:'#f59e0b' },
    { id:'hem_side', label:'Hem Side',           x: HH*0.7,   y: 0,         color:'#64748b' },
    { id:'hem_in',   label:'Hem Inseam',         x:-HH*0.7,   y: 0,         color:'#64748b' },
  ],

  collar: (m, W, H) => [
    { id:'CB',       label:'CB Collar',          x: 0,   y: 0,   color:'#ef4444', desc:'Centre Back of collar / neck edge' },
    { id:'tip_R',    label:'Collar Tip Right',   x: W,   y: H,   color:'#2563eb' },
    { id:'tip_L',    label:'Collar Tip Left',    x:-W,   y: H,   color:'#2563eb' },
    { id:'CF_R',     label:'CF Right',           x: W,   y: 0,   color:'#8b5cf6' },
    { id:'CF_L',     label:'CF Left',            x:-W,   y: 0,   color:'#8b5cf6' },
  ],
};

// ============================================================================
//  STANDARD MEASUREMENT DEFINITIONS
//  For each piece type: list of measurements as pairs of anchor IDs.
//  axis: 'y' = vertical distance only, 'x' = horizontal, 'euclidean' = true diagonal
// ============================================================================
export const TEMPLATE_MEASUREMENTS = {

  body_front: [
    { label: 'Body Length (HPS to Hem)',          from:'HPS_R',   to:'hem_R',   axis:'y', specKey:'bodyLength' },
    { label: 'Body Length (CB)',                  from:'HPS_L',   to:'hem_L',   axis:'y', specKey:'bodyLength' },
    { label: 'Half Chest (1" below armhole)',      from:'under_L', to:'under_R', axis:'x', specKey:'halfChest' },
    { label: 'Half Waist',                        from:'waist_L', to:'waist_R', axis:'x', specKey:'halfWaist' },
    { label: 'Shoulder Width (point to point)',   from:'HPS_L',   to:'HPS_R',  axis:'x', specKey:'shoulderWidth' },
    { label: 'Armhole Depth',                     from:'HPS_R',   to:'under_R', axis:'y', specKey:'armholeDepth' },
    { label: 'Front Neck Drop (HPS to CF)',       from:'HPS_R',   to:'CF_neck', axis:'y', specKey:'neckDepthFront' },
    { label: 'Half Bottom Hem',                   from:'hem_L',   to:'hem_R',  axis:'x', specKey:'halfHem' },
    { label: 'Side Seam Length',                  from:'under_R', to:'hem_R',  axis:'y', specKey:'sideSeam' },
  ],

  body_back: [
    { label: 'Body Length (HPS to Hem)',          from:'HPS_R',   to:'hem_R',  axis:'y', specKey:'bodyLength' },
    { label: 'Half Chest (1" below armhole)',     from:'under_L', to:'under_R',axis:'x', specKey:'halfChest' },
    { label: 'Half Waist',                        from:'waist_L', to:'waist_R',axis:'x', specKey:'halfWaist' },
    { label: 'Shoulder Width (point to point)',   from:'HPS_L',   to:'HPS_R',  axis:'x', specKey:'shoulderWidth' },
    { label: 'Armhole Depth',                     from:'HPS_R',   to:'under_R',axis:'y', specKey:'armholeDepth' },
    { label: 'Back Neck Rise',                    from:'HPS_R',   to:'CB_neck',axis:'y', specKey:'neckDepthBack' },
  ],

  sleeve: [
    { label: 'Sleeve Length (crown to cuff)',     from:'crown',   to:'cuff_C', axis:'y', specKey:'sleeveLength' },
    { label: 'Cap Height (crown to underarm)',    from:'crown',   to:'under_F',axis:'y', specKey:'capHeight' },
    { label: 'Sleeve Width at Underarm',         from:'under_B', to:'under_F',axis:'x', specKey:'sleeveWidth' },
    { label: 'Cuff Opening',                     from:'cuff_L',  to:'cuff_R', axis:'x', specKey:'cuffLength' },
    { label: 'Cap Circumference (approx)',       from:'under_F', to:'under_B',axis:'euclidean', specKey:'armholeCurve' },
  ],

  trouser_front: [
    { label: 'Front Rise (waist to crotch)',      from:'wst_side',to:'hip_side',axis:'y', specKey:'frontRise' },
    { label: 'Inseam (crotch to hem)',            from:'hip_side',to:'hem_in', axis:'y', specKey:'inseam' },
    { label: 'Half Waist',                        from:'wst_CF',  to:'wst_side',axis:'x', specKey:'halfWaist' },
    { label: 'Half Hip (at hip level)',           from:'hip_side',to:'crotch_tip',axis:'x', specKey:'halfHip' },
    { label: 'Half Thigh (1" below crotch)',      from:'hip_side',to:'crotch_tip',axis:'x', specKey:'halfThigh' },
    { label: 'Half Knee',                         from:'knee_R',  to:'knee_L', axis:'x', specKey:'halfKnee' },
    { label: 'Half Hem',                          from:'hem_side',to:'hem_in', axis:'x', specKey:'halfHem' },
  ],

  trouser_back: [
    { label: 'Back Rise',                         from:'wst_side',to:'hip_side',axis:'y', specKey:'backRise' },
    { label: 'Inseam (crotch to hem)',            from:'hip_side',to:'hem_in', axis:'y', specKey:'inseam' },
    { label: 'Half Waist (back)',                 from:'wst_CB',  to:'wst_side',axis:'x', specKey:'halfWaist' },
    { label: 'Half Hip (back)',                   from:'hip_side',to:'crotch_tip',axis:'x', specKey:'halfHip' },
    { label: 'Half Knee',                         from:'knee_R',  to:'knee_L', axis:'x', specKey:'halfKnee' },
    { label: 'Half Hem',                          from:'hem_side',to:'hem_in', axis:'x', specKey:'halfHem' },
  ],

  collar: [
    { label: 'Collar Length (CB to tip)',         from:'CB', to:'tip_R',    axis:'euclidean', specKey:'collarLength' },
    { label: 'Collar Height (stand to tip)',      from:'CF_R', to:'tip_R',  axis:'y',         specKey:'collarHeight' },
    { label: 'Neckline edge (half)',              from:'CF_R', to:'CF_L',   axis:'euclidean', specKey:'necklineHalf' },
  ],
};

// ── Calculate all standard measurements from anchor positions ────────────────
// anchors: array of { id, x, y } in pattern cm space
// template: 'body_front' | 'sleeve' | etc.
// Returns: { [label]: distanceCm }
export const calcMeasurementsFromAnchors = (anchors, template) => {
  const defs = TEMPLATE_MEASUREMENTS[template];
  if (!defs || !anchors?.length) return {};
  const map = {};
  anchors.forEach(a => (map[a.id] = a));
  const result = {};
  defs.forEach(def => {
    const a = map[def.from], b = map[def.to];
    if (!a || !b) return;
    let dist;
    if (def.axis === 'y')          dist = Math.abs(b.y - a.y);
    else if (def.axis === 'x')     dist = Math.abs(b.x - a.x);
    else                           dist = Math.hypot(b.x - a.x, b.y - a.y);
    result[def.label] = +dist.toFixed(2);
    if (def.specKey) result[def.specKey] = +dist.toFixed(2); // machine-readable key too
  });
  return result;
};
